import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMinus, faPlus } from "@fortawesome/pro-solid-svg-icons";
import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";

import Sheet from "components/sheet/Sheet";

import { ICartActions } from "redux/actions/cart";
import { dequeue, enqueue, IUIActions, showError, showSuccess } from "redux/actions/ui";
import { ICartState } from "redux/reducers/cart";
import { ButtonNew as Button } from "components/buttonNew";
import { useHistory } from "react-router";

import { StatusCode } from "api/protocols";
import "./tableSelection.scss";
import SwitchUserModal from "components/switchUserModal/SwitchUserModal";
import { IAuthState } from "redux/reducers/auth";
import { IAuthActions, setStaffAccountShift } from "redux/actions/auth";
import { SwitchUser, UserLogout } from "api/rpc";
import { Select } from "components/select/index";
import { XYCoord } from "react-dnd";

import { useTranslation } from "react-i18next";
import ReactDOM from "react-dom";
import { useDispatch } from "react-redux";
import { FACILITY_DISPATCH_TARGET_FACILITY } from "redux/facilityDispatchMeta";
import { useAppSelector } from "hooks/redux";
import {
  GetFloorPlans,
  GetTable,
  GetTableElements,
  GetTableReceipt,
  IFloorPlan,
  ITable,
  ITableElement,
  PutOpenTable,
  PutTable,
} from "api/rpc/2024-04/facilityAdmin/tables/tables";
import { Badge } from "components/badge/Badge";
import moment from "moment";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import useModal from "hooks/modals/useModal";
import Portal from "elements/Portal";
import Popup from "components/popup/Popup";
import { PostStaffAccountShiftClockIn } from "api/rpc/2024-04/facilityAdmin/client/shifts";
import { IStaffAccountShift } from "redux/reducers/models/shift";
import TableServiceToolTip from "components/tableServiceToolTip/tableServiceToolTip";
import { loadAdminFacility } from "redux/actions/facility";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { TABLET_WIDTH } from "helpers/ScreenSizes";
import { deriveTableShortTitle, printDesktopKitchenChit, printDesktopReceipt } from "helpers/Helpers";
import TransferTableSheet, { ITransferTableSheetState } from "components/transferTableSheet/TransferTableSheet";
import EditSeatDetails, { isSeatEditableForGuestDetails } from "components/editSeatDetails/EditSeatDetails";
import PrintChit from "components/printChit/PrintChit";
import TableServiceSendLineItemsModal from "./LineItemModals/TableServiceSendLineItemsModal";
import CartAuditTrailDrawer from "../register/registerNew/CartAuditTrailDrawer";
import { GetFacilityAdmin } from "api/rpc/2024-04/facilityAdmin/client/admin/admin";
import { IUser } from "redux/reducers/models/user";
import { ICustomer } from "redux/reducers/models/customer";
import { ICart } from "redux/reducers/models/cart";
import { PutCart } from "api/rpc/2024-04/facilityAdmin/cart/cart";
import { PostKitchenChit } from "api/rpc/2024-04/facilityAdmin/facility/kitchen/chit";
import { GetPrintBulkReceipts } from "api/rpc/2024-04/facilityAdmin/order/order";
import { IChit } from "hooks/usePOSPrinter/usePOSPrinter";

interface IProps {
  cartStore: ICartState;
  cartActions: ICartActions;
  uiActions: IUIActions;
  authStore: IAuthState;
  authActions: IAuthActions;
}

interface IState {
  tableElements: ITableElement[];
  floorPlanRef: React.MutableRefObject<HTMLDivElement>;
  floorPlanWidth: number;
  floorPlanHeight: number;
  floorPlanGridSquareSideLength: number;
  floorPlans: IFloorPlan[];
  selectedFloorPlan: IFloorPlan;
  seatsAmount: number;
  seatsAmountVisible: boolean;
  selectedTable: Record<string, any>;
  setCartActive: boolean;
  activeUserCode: string;
  displayActiveUserSheet: boolean;
}

//Positioning -> [top, right, bottom, left]
//Key is the number of seats on the table
const RECTANGULAR_HORIZONTAL_LAYOUT: Record<number, [number, number, number, number]> = {
  2: [2, 0, 0, 0],
  4: [2, 0, 2, 0],
  6: [2, 1, 2, 1],
  8: [3, 1, 3, 1],
  10: [4, 1, 4, 1],
};

export default function TableSelection(props: IProps) {
  const { cartStore, cartActions, uiActions, authStore, authActions } = props;
  const { facilityStore } = useAppSelector(store => store);
  const [timeNow, setTimeNow] = useState(moment());
  const { state: clockInModal, updateModal: updateClockInModal, closeModal: closeClockInModal } = useModal();

  const dispatch = useDispatch();
  const windowSize = useWindowSize();
  const isTabletOrBelow = (windowSize.width ?? TABLET_WIDTH + 1) <= TABLET_WIDTH;

  const history = useHistory();
  const { t } = useTranslation();
  const { Option } = Select;

  const [state, setState] = useState<IState>({
    tableElements: [],
    floorPlanRef: useRef(),
    floorPlanWidth: 0,
    floorPlanHeight: 0,
    floorPlanGridSquareSideLength: 0,
    floorPlans: [],
    selectedFloorPlan: undefined,
    seatsAmount: 1,
    seatsAmountVisible: false,
    selectedTable: null,
    setCartActive: false,
    activeUserCode: "",
    displayActiveUserSheet: false,
  });

  const [switchUserAfterTransaction, setSwitchUserAfterTransaction] = useState<boolean>(false);
  const [viewCartAudit, setViewCartAudit] = useState(false);

  const { state: transferTable, updateModal: updateTransferTable } = useModal<{ users: Array<IUser> }>({
    users: [],
    clearOnClose: false,
  });
  const {
    state: seatDetails,
    updateModal: updateSeatDetails,
    closeModal: closeSeatDetails,
  } = useModal<{ customer: ICustomer; description: string }>({ customer: null, description: "" });
  const { state: sendChits, updateModal: updateSendChits, closeModal: cancelSendChits } = useModal();
  const { state: printChitsState, updateModal: updatePrintChits, closeModal: cancelPrintChits } = useModal();

  useEffect(() => {
    void loadTableElements();
    void loadFloorPlans();

    const interval = setInterval(() => {
      //Set current time every minute to update table open times
      setTimeNow(moment());
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (state?.selectedFloorPlan) {
      interval = setInterval(() => {
        // Reload tables every minute
        props.cartActions.loadTables(false, state.selectedFloorPlan.id);
      }, 60000);
    }
    return () => clearInterval(interval);
  }, [state?.selectedFloorPlan]);

  useEffect(() => {
    const switchUserAfterTransaction = localStorage.getItem("switch_user_after_transaction");
    if (switchUserAfterTransaction === "true") {
      setSwitchUserAfterTransaction(true);
    }
  }, [localStorage.getItem("switch_user_after_transaction")]);

  async function loadTableElements() {
    const getTableElementsResponse = await GetTableElements(true);

    if (getTableElementsResponse.status !== StatusCode.OK || getTableElementsResponse.data === undefined) {
      uiActions.showError(t("secure.facility.table_service.table_selection.001"));
      return;
    }

    setState(prevState => ({
      ...prevState,
      tableElements: getTableElementsResponse.data,
    }));
  }

  async function loadFloorPlans() {
    const register = JSON.parse(localStorage.getItem("register"));

    const getFloorPlansResponse = await GetFloorPlans(null, true);

    if (getFloorPlansResponse.status !== StatusCode.OK) {
      uiActions.showError(t("secure.facility.table_service.table_selection.002"));
      return;
    }

    const default_floor_plan_id = register?.default_floor_plan_id;
    let defaultFloorPlan: IFloorPlan;

    if (facilityStore?.selectedFloorPlanId) {
      defaultFloorPlan = getFloorPlansResponse.data?.find(
        floorPlan => floorPlan.id === facilityStore?.selectedFloorPlanId,
      );
    } else {
      defaultFloorPlan = getFloorPlansResponse.data?.find(
        floorPlan =>
          floorPlan.id === default_floor_plan_id || floorPlan?.register_group_id === register?.register_group_id,
      );
    }

    setState(prevState => ({
      ...prevState,
      floorPlans: getFloorPlansResponse.data,
      selectedFloorPlan: defaultFloorPlan ?? getFloorPlansResponse.data?.[0],
    }));
  }

  useEffect(() => {
    if (state.selectedFloorPlan?.id) {
      props.cartActions.loadTables(true, state.selectedFloorPlan.id);
    }
  }, [state.selectedFloorPlan?.id]);

  useEffect(() => {
    function handleResize() {
      setState(prevState => ({
        ...prevState,
        floorPlanWidth: state.floorPlanRef.current.clientWidth,
      }));
    }

    if (state.floorPlanRef?.current) {
      handleResize();
      window.addEventListener("resize", handleResize);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [state.floorPlanRef?.current]);

  useEffect(() => {
    if (
      state.floorPlanWidth > 0 &&
      state.selectedFloorPlan?.grid_width > 0 &&
      state.selectedFloorPlan?.grid_height > 0
    ) {
      const gridProportion = state.selectedFloorPlan.grid_height / state.selectedFloorPlan.grid_width;

      setState(prevState => ({
        ...prevState,
        floorPlanHeight: state.floorPlanWidth * gridProportion,
        floorPlanGridSquareSideLength: state.floorPlanWidth / state.selectedFloorPlan.grid_width,
      }));
    }
  }, [state.floorPlanWidth, cartStore.tables]);

  function handleSelectFloorPlan(id: number) {
    const selectedFloorPlan = state.floorPlans.find(floorPlan => floorPlan.id === id);

    setState(prevState => ({ ...prevState, selectedFloorPlan }));

    dispatch({
      type: "facility.update",
      meta: { facilityDispatchTarget: FACILITY_DISPATCH_TARGET_FACILITY },
      payload: {
        selectedFloorPlanId: id,
      },
    });
  }

  async function handleClockIn() {
    dispatch(enqueue());
    const res = await PostStaffAccountShiftClockIn(null, false);
    dispatch(dequeue());
    if (res?.status !== StatusCode.OK) {
      dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error clocking in")));
      return;
    }
    dispatch(showSuccess(t("Successfully clocked in")));
    dispatch(setStaffAccountShift(res?.data as IStaffAccountShift));
    void closeClockInModal();
  }

  async function selectTable(table: ITable) {
    if (requiresTableServiceClockIn()) {
      void updateClockInModal({ isOpen: true });
      return;
    }

    if (
      !!table.open === false ||
      table.user_id === authStore.user.id ||
      authStore.user.permissions.table_service_table_override
    ) {
      dispatch(enqueue());
      const tableRes = await GetTable({ id: table?.id }, false);
      if (tableRes?.status !== StatusCode.OK || tableRes?.data?.length < 1) {
        dispatch(dequeue());
        dispatch(showError(typeof tableRes?.data === "string" ? tableRes?.data : t("Error getting table status")));
        return;
      }
      const updatedTable = tableRes?.data[0];

      //User does not have access to the table
      if (
        updatedTable.open &&
        updatedTable?.user_id !== authStore.user.id &&
        !authStore.user.permissions.table_service_table_override
      ) {
        //Reload tables
        props.cartActions.loadTables(false, state.selectedFloorPlan.id);
        dispatch(dequeue());
        uiActions.showError(t("secure.facility.table_service.table_selection.003"));
        return;
      }

      if (updatedTable?.open) {
        // eslint-disable-next-line @typescript-eslint/await-thenable
        await cartActions.loadActiveTableNew(table?.id, false);
        dispatch(dequeue());
        history.push("/admin/table-service");
      } else {
        dispatch(dequeue());
        setState(prevState => ({ ...prevState, selectedTable: updatedTable }));
        void handleOpenTable(updatedTable);
      }
    } else {
      uiActions.showError(t("secure.facility.table_service.table_selection.003"));
    }
  }

  async function handleOpenTable(table: Record<string, any>) {
    const register = JSON.parse(localStorage.getItem("register"));
    if (register) {
      const openTableRes = await PutOpenTable(
        {
          register_id: register?.id,
          table_id: table?.id,
          number_of_seats: 1,
        },
        true,
      );

      if (openTableRes.status !== StatusCode.OK) {
        uiActions.showError(t("secure.facility.table_service.table_selection.004"));
        return;
      }

      uiActions.showSuccess(t("secure.facility.table_service.table_selection.013"));
      setState(prevState => ({ ...prevState, seatsAmountVisible: false, seatsAmount: 1, selectedTable: null }));
      await selectTable(openTableRes?.data);
    } else {
      uiActions.showError(t("secure.facility.table_service.table_selection.005"));
    }
  }

  function backToDashboard() {
    history.push("/admin/dashboard");
  }

  function handleOpenActiveUserSheet() {
    setState(prevState => ({ ...prevState, activeUserCode: "", displayActiveUserSheet: true }));
  }

  function handleCloseActiveUserSheet() {
    ReactDOM.unstable_batchedUpdates(() => {
      setSwitchUserAfterTransaction(false);
      setState(prevState => ({ ...prevState, activeUserCode: "", displayActiveUserSheet: false }));
    });
  }

  function isPositiveInteger(value: string) {
    const positiveIntegerRegex = /^[1-9][0-9]*$/;
    return positiveIntegerRegex.test(value);
  }

  async function handleUpdateActiveUser() {
    if (isPositiveInteger(state.activeUserCode)) {
      const switchUserResponse = await SwitchUser({ quick_code: parseInt(state.activeUserCode) }, true);

      if (switchUserResponse.status !== StatusCode.OK) {
        props.uiActions.showError(t("secure.facility.table_service.table_selection.006"));
        return;
      }

      authActions.userLogin(switchUserResponse.data.user);
      dispatch(loadAdminFacility({ client: true }, false));
      localStorage.setItem("localeLanguage", switchUserResponse.data.user.primary_locale);
      localStorage.setItem("localeLanguageWithCountry", switchUserResponse.data.user.primary_locale_country);
      localStorage.setItem("switch_user_after_transaction", "false");

      props.uiActions.showSuccess(t("secure.facility.table_service.table_selection.007"));

      handleCloseActiveUserSheet();
    }
  }

  function handleActiveUserCodeEnter(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.code === "Enter" || e.code === "NumpadEnter") {
      void handleUpdateActiveUser();
    }
  }

  function handleActiveUserCodeChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { value } = e.target;
    setState(prevState => ({ ...prevState, activeUserCode: value }));
  }

  function getTableImageSource(table: ITable) {
    const tableElement = state.tableElements.find(tableElement => tableElement.id === table.table_element_id);
    const src = tableElement?.image_src ?? "";

    return src;
  }

  function getTableDimensions(table: ITable) {
    const tableElement = state.tableElements.find(tableElement => tableElement.id === table.table_element_id);

    if (!tableElement) {
      return { width: 0, height: 0 };
    } else {
      return {
        width: tableElement.grid_width * state.floorPlanGridSquareSideLength,
        height: tableElement.grid_height * state.floorPlanGridSquareSideLength,
      };
    }
  }

  function getTableCoordinates(table: ITable): XYCoord {
    const tableElement = state.tableElements.find(tableElement => tableElement.id === table.table_element_id);
    if (tableElement) {
      const xCoordinate =
        table.x_coordinate * state.floorPlanGridSquareSideLength -
        (tableElement.grid_width * state.floorPlanGridSquareSideLength) / 2;

      const yCoordinate =
        table.y_coordinate * state.floorPlanGridSquareSideLength -
        (tableElement.grid_height * state.floorPlanGridSquareSideLength) / 2;

      return { x: xCoordinate, y: yCoordinate };
    } else {
      return { x: 0, y: 0 };
    }
  }

  function getCenterOfTableCoordinates(table: ITable) {
    const tableElement = state.tableElements.find(tableElement => tableElement.id === table.table_element_id);
    if (tableElement) {
      const xCoordinate = (tableElement.grid_width * state.floorPlanGridSquareSideLength) / 2;
      const yCoordinate = (tableElement.grid_height * state.floorPlanGridSquareSideLength) / 2;

      return { xCenter: xCoordinate, yCenter: yCoordinate };
    } else {
      return { xCenter: 0, yCenter: 0 };
    }
  }

  async function handleLogoutUser() {
    await UserLogout(true);
    localStorage.setItem("switch_user_after_transaction", "false");
    authActions.userLogout();
    history.push("/");
  }

  function formatTime(timeDifference: moment.Duration) {
    const hours = timeDifference.hours().toString().padStart(2, "0");
    const minutes = timeDifference.minutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  }

  function elapsedTime(tableOpenTime: string) {
    if (tableOpenTime) {
      const tableOpenTimeLocal = moment.utc(tableOpenTime).local();
      //Get the difference in current time & table open time
      const differenceTime = moment.duration(timeNow.diff(tableOpenTimeLocal));
      if (differenceTime?.days() >= 1) {
        return `${differenceTime?.days()} ${differenceTime?.days() > 1 ? t("Days") : t("Day")}`;
      }
      const formattedElapsedTime = formatTime(differenceTime);
      return formattedElapsedTime;
    } else {
      return "00:00";
    }
  }

  function resolveRectangleLayout(tableElement: ITableElement) {
    //Special cases
    if (tableElement.title.includes("Rectangle / 6 Seat / Two Sides")) {
      return [3, 0, 3, 0];
    }

    return RECTANGULAR_HORIZONTAL_LAYOUT[tableElement.seats];
  }

  function getRoundTableSeatPositions({
    seatCount,
    centerX,
    centerY,
    radius,
    rotation,
  }: {
    seatCount: number;
    centerX: number;
    centerY: number;
    radius: number;
    rotation: number;
  }) {
    const seats = [];
    const angleStep = 360 / seatCount;
    const startAngle = -90;

    for (let i = 0; i < seatCount; i++) {
      const angleDeg = startAngle + i * angleStep + rotation;
      const angleRad = (angleDeg * Math.PI) / 180;

      seats.push({
        seatNumber: i + 1,
        x: centerX + radius * Math.cos(angleRad),
        y: centerY + radius * Math.sin(angleRad),
      });
    }

    return seats;
  }

  function getRectangleTableSeatPositions({
    seatCount,
    centerX,
    centerY,
    width,
    height,
    rotation,
    tableElement,
    offset = 0,
  }: {
    seatCount: number;
    centerX: number;
    centerY: number;
    width: number;
    height: number;
    rotation: number;
    tableElement: ITableElement;
    offset?: number;
  }) {
    const layout = resolveRectangleLayout(tableElement);
    if (!layout) {
      return [];
    }
    const tenSeat = seatCount === 10;
    const topBottomAdjustment = tenSeat ? 0.46 : 0.5;
    const sidesAdjustment = tenSeat ? 0.9 : 0.85;
    const [top, right, bottom, left] = layout;
    const seats = [];

    const halfW = width / 2 + offset;
    const halfH = height / 2 + offset;

    let seatNumber = 1;

    // Top
    for (let i = 0; i < top; i++) {
      seats.push({
        seatNumber: seatNumber++,
        x: centerX - halfW + ((i + 1) * width) / (top + 1),
        y: centerY - halfH * topBottomAdjustment,
      });
    }

    // Right
    for (let i = 0; i < right; i++) {
      seats.push({
        seatNumber: seatNumber++,
        x: centerX + halfW * sidesAdjustment,
        y: centerY - halfH + ((i + 1) * height) / (right + 1),
      });
    }

    // Bottom
    for (let i = bottom - 1; i >= 0; i--) {
      seats.push({
        seatNumber: seatNumber++,
        x: centerX - halfW + ((i + 1) * width) / (bottom + 1),
        y: centerY + halfH * topBottomAdjustment,
      });
    }

    // Left
    for (let i = left - 1; i >= 0; i--) {
      seats.push({
        seatNumber: seatNumber++,
        x: centerX - halfW * sidesAdjustment,
        y: centerY - halfH + ((i + 1) * height) / (left + 1),
      });
    }

    // Rotation
    const rad = (rotation * Math.PI) / 180;

    return seats.map(seat => {
      const dx = seat.x - centerX;
      const dy = seat.y - centerY;

      return {
        ...seat,
        x: centerX + dx * Math.cos(rad) - dy * Math.sin(rad),
        y: centerY + dx * Math.sin(rad) + dy * Math.cos(rad),
      };
    });
  }

  function getTableElement(tableElementId: number) {
    const tableElement = state.tableElements.find(tableElement => tableElement.id === tableElementId);
    return tableElement;
  }

  function requiresTableServiceClockIn() {
    if (!facilityStore?.teeSheetSettings?.table_service_clock_in) {
      return false;
    }
    // Not clocked in, or already clocked out
    return !authStore?.shift || (!!authStore?.shift?.clock_in_at && !!authStore?.shift?.clock_out_at);
  }

  function canAccessTable(table: ITable) {
    return (
      !!table.open && (table.user_id === authStore.user.id || !!authStore.user.permissions.table_service_table_override)
    );
  }

  async function loadTableForAction(table: ITable): Promise<{ table: ITable; cart: ICart | null } | null> {
    if (requiresTableServiceClockIn()) {
      void updateClockInModal({ isOpen: true });
      return null;
    }

    if (!canAccessTable(table)) {
      uiActions.showError(t("secure.facility.table_service.table_selection.003"));
      return null;
    }

    dispatch(enqueue());
    const tableRes = await GetTable({ id: table.id, carts: true, orders: true }, false);
    dispatch(dequeue());

    if (tableRes?.status !== StatusCode.OK || tableRes?.data?.length < 1) {
      dispatch(showError(typeof tableRes?.data === "string" ? tableRes?.data : t("Error getting table status")));
      return null;
    }

    const updatedTable = tableRes.data[0];

    if (
      updatedTable.open &&
      updatedTable.user_id !== authStore.user.id &&
      !authStore.user.permissions.table_service_table_override
    ) {
      props.cartActions.loadTables(false, state.selectedFloorPlan.id);
      uiActions.showError(t("secure.facility.table_service.table_selection.003"));
      return null;
    }

    if (!updatedTable.open) {
      uiActions.showError(t("secure.facility.table_service.table_selection.003"));
      return null;
    }

    const cart = updatedTable.carts?.find(seatCart => seatCart.status === "open") ?? updatedTable.carts?.[0] ?? null;

    cartActions.setActiveTable(updatedTable);
    // eslint-disable-next-line @typescript-eslint/await-thenable
    await cartActions.loadTableCart({ tableCart: cart }, false);

    return { table: updatedTable, cart };
  }

  function getSendChitIds(activeTable: ITable) {
    let lineItemIds: Array<number> = [];
    if (activeTable?.carts) {
      activeTable.carts.forEach(cart => {
        if (cart?.status !== "complete" && (!cart?.order || cart?.order?.financial_status === "unpaid")) {
          const lineItemsToAdd =
            cart?.line_items
              ?.filter(
                filteredLineItem =>
                  !filteredLineItem.parent_id &&
                  filteredLineItem?.kitchen_status !== "sent" &&
                  (filteredLineItem?.product?.meta?.kitchen_location_id ||
                    filteredLineItem?.product?.meta?.kitchen_location_id_2 ||
                    filteredLineItem?.product?.meta?.kitchen_location_id_3),
              )
              ?.map(lineItem => lineItem?.id) ?? [];
          lineItemIds = [...lineItemIds, ...lineItemsToAdd];
        }
      });
    }
    return lineItemIds;
  }

  async function handleContextTransferTable(table: ITable) {
    const loaded = await loadTableForAction(table);
    if (!loaded) {
      return;
    }

    const usersRes = await GetFacilityAdmin(null, true);
    if (usersRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.table_service.table_service_cart.table_service_cart.001")));
      return;
    }

    void updateTransferTable({ isOpen: true, users: usersRes?.data as unknown as IUser[] });
  }

  async function handleContextSendKitchen(table: ITable) {
    const loaded = await loadTableForAction(table);
    if (!loaded) {
      return;
    }
    void updateSendChits({ isOpen: true });
  }

  async function handleContextSeatDetails(table: ITable) {
    const loaded = await loadTableForAction(table);
    if (!loaded?.table?.carts?.length) {
      if (loaded) {
        dispatch(showError(t("No active seats")));
      }
      return;
    }

    const editableCart = loaded.table.carts.find(isSeatEditableForGuestDetails) ?? null;
    if (!editableCart) {
      dispatch(showError(t("No active seats")));
      return;
    }

    if (editableCart.id !== loaded.cart?.id) {
      // eslint-disable-next-line @typescript-eslint/await-thenable
      await cartActions.loadTableCart({ tableCart: editableCart }, false);
    }

    void updateSeatDetails({
      isOpen: true,
      customer: editableCart.customer,
      description: editableCart.description ?? "",
    });
  }

  async function handleSeatDetailsSeatChange(cart: ICart) {
    if (!isSeatEditableForGuestDetails(cart)) {
      return;
    }

    void updateSeatDetails({
      isOpen: true,
      customer: cart.customer,
      description: cart.description ?? "",
    });
    // eslint-disable-next-line @typescript-eslint/await-thenable
    await cartActions.loadTableCart({ tableCart: cart }, false);
  }

  async function handleContextPrintChit(table: ITable) {
    const loaded = await loadTableForAction(table);
    if (!loaded) {
      return;
    }
    void updatePrintChits({ isOpen: true });
  }

  async function handleContextPrintReceipts(table: ITable) {
    const loaded = await loadTableForAction(table);
    if (!loaded?.table?.carts?.length) {
      if (loaded) {
        dispatch(showError(t("Error printing receipts")));
      }
      return;
    }

    const printableCarts = loaded.table.carts.filter(cart => cart?.status !== "void" && cart?.status !== "complete");
    if (!printableCarts.length) {
      dispatch(showError(t("Error printing receipts")));
      return;
    }

    const receipts: Array<{ id: number; type: "cart" | "order" }> = printableCarts.map(cart =>
      cart?.order && cart?.order?.financial_status !== "unpaid"
        ? { id: cart.order_id, type: "order" }
        : { id: cart.id, type: "cart" },
    );
    const receiptRes = await GetPrintBulkReceipts({ receipts }, true);
    if (receiptRes.status === StatusCode.OK) {
      printDesktopReceipt(facilityStore.facility?.id, receiptRes.data.toString() as string);
    } else {
      dispatch(showError(t("Error printing receipts")));
    }
  }

  async function handleContextSeatActivity(table: ITable) {
    const loaded = await loadTableForAction(table);
    if (!loaded?.cart) {
      if (loaded) {
        dispatch(showError(t("No active seats")));
      }
      return;
    }
    setViewCartAudit(true);
  }

  async function handleTransferTable(transferTableInfo: ITransferTableSheetState) {
    const activeTable = cartStore.activeTable;
    if (transferTableInfo?.selectedUserId !== activeTable?.user_id) {
      const transferRes = await PutTable({ id: activeTable?.id, user_id: transferTableInfo?.selectedUserId }, true);

      if (transferRes?.status !== StatusCode.OK) {
        dispatch(showError(t("secure.facility.table_service.table_service_cart.table_service_cart.078")));
        return;
      }
      dispatch(showSuccess(t("secure.facility.table_service.table_service_cart.table_service_cart.079")));
      void updateTransferTable({ isOpen: false });
      props.cartActions.loadTables(false, state.selectedFloorPlan.id);
    }
  }

  async function handleUpdateMultiCartDetails(cartsToUpdate: Record<string, any>) {
      dispatch(enqueue());
      const responses = await Promise.all(Object.keys(cartsToUpdate)?.map(cartId => PutCart({
        id: cartId,
        customer_id: cartsToUpdate[cartId]?.selectedCustomer ? cartsToUpdate[cartId]?.selectedCustomer?.id : null,
        description: cartsToUpdate[cartId]?.cartDescription ? cartsToUpdate[cartId]?.cartDescription: null,
      }, false)));
      dispatch(dequeue());
      if (responses?.some(res => res?.status !== StatusCode.OK)) {
        dispatch(showError(t("secure.facility.table_service.table_service_cart.table_service_cart.071")));
      } else {
        dispatch(showSuccess(t("secure.facility.table_service.table_service_cart.table_service_cart.072")));
      }
      void closeSeatDetails();
      props.cartActions.loadTables(false, state.selectedFloorPlan.id);
  }

  async function handleSendChit(lineItemIds: number[]) {
    void cancelSendChits();
    const activeTable = cartStore.activeTable;
    const res = await PostKitchenChit({ table_id: activeTable?.id, line_item_ids: lineItemIds }, true);
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.table_service.table_service_cart.table_service_cart.051")));
      return;
    }

    const chits = res?.data;
    let chitCount = 0;
    const tasks = [];

    for (const chit of chits) {
      chit.chit_number = ++chitCount;
      chit.total_chits = chits.length;
      tasks.push(printDesktopKitchenChit(chit as IChit, t));
    }

    Promise.all(tasks)
      .then(() => dispatch(showSuccess(t("secure.facility.table_service.table_service_cart.table_service_cart.052"))))
      .catch(error => dispatch(showError(error as string)));
  }

  async function handlePrintReceipt(printChitInfo: { addedLineItems: Array<number> }) {
    const receiptRes = await GetTableReceipt(
      { table_id: cartStore.activeTable?.id, line_item_ids: printChitInfo?.addedLineItems },
      true,
    );
    if (receiptRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.table_service.table_service_cart.table_service_cart.053")));
      return;
    }
    printDesktopReceipt(facilityStore.facility?.id, receiptRes.data.toString());
    void cancelPrintChits();
  }

  function getTableMenuSections(table: ITable) {
    const hasAccess = canAccessTable(table);

    return [
      [
        {
          icon: "right-left" as const,
          title: t("Transfer Table"),
          disabled: !hasAccess,
          action: () => handleContextTransferTable(table),
        },
        {
          icon: "paper-plane" as const,
          title: t("Send to Kitchen"),
          disabled: !hasAccess,
          action: () => handleContextSendKitchen(table),
        },
      ],
      [
        {
          icon: "file-lines" as const,
          title: t("Add Guest Name"),
          disabled: !hasAccess,
          action: () => handleContextSeatDetails(table),
        },
      ],
      [
        {
          icon: "receipt" as const,
          title: t("Print Chit"),
          disabled: !hasAccess,
          action: () => handleContextPrintChit(table),
        },
        {
          icon: "print" as const,
          title: t("Print All Receipts"),
          disabled: !hasAccess,
          action: () => handleContextPrintReceipts(table),
        },
        {
          icon: "list-ul" as const,
          title: t("Seat Activity"),
          disabled: !hasAccess,
          action: () => handleContextSeatActivity(table),
        },
      ],
    ];
  }

  const floorPlanTopOffset =
    isTabletOrBelow && state.floorPlanGridSquareSideLength > 0
      ? Math.max(40, Math.round(state.floorPlanGridSquareSideLength * 1.75))
      : 0;

  return (
    <div className="table-selection">
      <div className="table-selection-top-bar-container">
        <div className="table-selection-top-bar-left">
          <Button size="medium" type="secondary" onClick={backToDashboard}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </Button>
          <p className="text-semibold text-sm">{t("Table Selection")}</p>
        </div>
        <Select
          className="table-selection-top-bar-floor-select"
          onChange={handleSelectFloorPlan}
          defaultValue={state.selectedFloorPlan?.id}
        >
          {state.floorPlans?.map((floorPlan, index) => {
            return (
              <Option key={index} value={floorPlan.id}>
                {floorPlan.title}
              </Option>
            );
          })}
        </Select>
        <div className="table-selection-user-container" onClick={handleOpenActiveUserSheet}>
          <div className="table-selection-user-avatar">
            {authStore?.user?.profile_image_source ? (
              <img src={authStore.user.profile_image_source} alt={t("profile image")} />
            ) : (
              <p>
                {authStore?.user?.first_name?.charAt(0)}
                {authStore?.user?.last_name?.charAt(0)}
              </p>
            )}
          </div>

          <div className="table-selection-user-info">
            <p>
              {authStore?.user?.first_name} {authStore?.user?.last_name}
            </p>
            <span>{authStore?.user?.email}</span>
          </div>
        </div>
      </div>
      {state.tableElements?.length > 0 && (
        <div className="table-selection-floor-plan-tables-container">
          <div
            ref={state.floorPlanRef}
            className="table-selection-floor-plan-tables"
            style={{
              height: state.floorPlanHeight,
              marginTop: floorPlanTopOffset || undefined,
            }}
          >
            {cartStore.tables?.map((table, index: number) => {
              const { x, y } = getTableCoordinates(table);
              const { xCenter, yCenter } = getCenterOfTableCoordinates(table);
              const { width, height } = getTableDimensions(table);
              const seatMarkerInset = state.floorPlanGridSquareSideLength * 0.48;
              const seatRadius = isTabletOrBelow ? ((Math.max(width, height) / 2) - seatMarkerInset) : ((Math.max(width, height) / 2 )+ state.floorPlanGridSquareSideLength * -0.55);
              const isCompact = state.floorPlanGridSquareSideLength < 28;
              const seatLabelSize = isTabletOrBelow
                ? Math.max(6, Math.round(state.floorPlanGridSquareSideLength * 0.35))
                : 12;
              const shortNameFontSize = isTabletOrBelow
                ? Math.max(8, Math.round(state.floorPlanGridSquareSideLength * 0.45))
                : Math.max(8, Math.round(state.floorPlanGridSquareSideLength / 2));
              const tableElement = getTableElement(table?.table_element_id);
              let rectangle = false;
              let seats = null;

              if (tableElement) {
                rectangle = tableElement?.title?.toLowerCase()?.includes("rectangle");
                seats = rectangle
                  ? getRectangleTableSeatPositions({
                      seatCount: tableElement?.seats,
                      centerX: x + xCenter,
                      centerY: y + yCenter,
                      width,
                      height,
                      rotation: table.rotation,
                      tableElement,
                      offset: 0,
                    })
                  : getRoundTableSeatPositions({
                      seatCount: tableElement?.seats,
                      centerX: x + xCenter,
                      centerY: y + yCenter,
                      radius: seatRadius,
                      rotation: table.rotation,
                    });
              }

              let badgeTop = 0;
              if (isTabletOrBelow) {
                const badgeGap = Math.max(8, state.floorPlanGridSquareSideLength * 0.3);
                badgeTop = seats?.length
                  ? Math.min(...seats.map((seat: { y: number }) => seat.y)) - seatLabelSize / 2 - badgeGap
                  : y - badgeGap;
              }

              const centerLabelStyle = {
                position: "absolute" as const,
                userSelect: "none" as const,
                pointerEvents: "none" as const,
                left: x + xCenter,
                top: y + yCenter,
                transform: "translate(-50%, -50%)",
                fontSize: `${shortNameFontSize}px`,
              };

              return (
                <TableServiceToolTip
                  key={`table-${index}`}
                  table={table}
                  x={x}
                  y={y}
                  width={width}
                  height={height}
                  onClick={() => selectTable(table)}
                  menuSections={table.open ? getTableMenuSections(table) : undefined}
                >
                  <img
                    className={classNames({
                      "table-selection-floor-plan-table-blocked":
                        table?.user_id !== authStore?.user?.id &&
                        table?.open &&
                        !authStore?.user?.permissions?.table_service_table_override,
                      "table-selection-floor-plan-table-success":
                        (table?.user_id === authStore?.user?.id ||
                          authStore?.user?.permissions?.table_service_table_override) &&
                        table?.open,
                      "table-selection-floor-plan-table-closed": !table?.open,
                    })}
                    src={getTableImageSource(table)}
                    draggable={false}
                    onClick={() => selectTable(table)}
                    style={{
                      position: "absolute",
                      cursor: "pointer",
                      left: x,
                      top: y,
                      width: width,
                      height: height,
                      transform: `rotate(${table.rotation}deg)`,
                      transformOrigin: "center",
                    }}
                  />
                  {state?.selectedFloorPlan?.display_seat_numbers && seats
                    ? seats?.map((seat: any) => (
                        <div
                          key={`table-${table.id}-seat-${seat.seatNumber}`}
                          className="table-selection-seat-number"
                          style={{
                            left: seat.x,
                            top: seat.y,
                            fontSize: seatLabelSize,
                          }}
                        >
                          {seat.seatNumber}
                        </div>
                      ))
                    : null}
                  {state.floorPlanGridSquareSideLength > 0 && (
                    <>
                      {isTabletOrBelow && (
                        <div
                          className="table-selection-badge-anchor"
                          style={{
                            position: "absolute",
                            userSelect: "none",
                            pointerEvents: "none",
                            left: x + xCenter,
                            top: badgeTop,
                            transform: "translate(-50%, -100%)",
                          }}
                        >
                          <Badge size={isCompact ? "xsmall" : "medium"} colour="#175CD3" backgroundColour="#EFF8FF">
                            {elapsedTime(table?.open_time)}
                          </Badge>
                        </div>
                      )}
                      <div className="table-selection-short-name-anchor" style={centerLabelStyle}>
                        {isTabletOrBelow ? (
                          <div className="table-selection-short-name">
                            {deriveTableShortTitle(table.title) || table.short_title}
                          </div>
                        ) : (
                          <div className="table-selection-timer-container">
                            <Badge size="medium" colour="#175CD3" backgroundColour="#EFF8FF">
                              {elapsedTime(table?.open_time)}
                            </Badge>
                            <div className="table-selection-short-name table-selection-short-name--desktop">
                              {deriveTableShortTitle(table.title) || table.short_title}
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </TableServiceToolTip>
              );
            })}
          </div>
        </div>
      )}

      <Sheet
        title={t("secure.facility.table_service.table_selection.008")}
        open={state.seatsAmountVisible}
        size="small"
        closable
        onCancel={() =>
          setState(prevState => ({ ...prevState, seatsAmount: 1, seatsAmountVisible: false, selectedTable: null }))
        }
        onOk={handleOpenTable}
        okText={t("secure.facility.table_service.table_selection.009")}
        okDisabled={state.seatsAmount < 1 ? true : false}
      >
        <div className="flex flex-row justify-between items-center mt-4 h-full mb-auto">
          <Button
            onClick={() => {
              setState(prevState => ({
                ...prevState,
                seatsAmount: prevState.seatsAmount <= 1 ? 1 : prevState.seatsAmount - 1,
              }));
            }}
          >
            <FontAwesomeIcon icon={faMinus} />
          </Button>
          <span className="text-2xl">
            {state.seatsAmount}{" "}
            {`${t("secure.facility.table_service.table_selection.011")}${state.seatsAmount > 1 ? "s" : ""}`}
          </span>
          <Button
            onClick={() => {
              setState(prevState => ({ ...prevState, seatsAmount: prevState.seatsAmount + 1 }));
            }}
          >
            <FontAwesomeIcon icon={faPlus} />
          </Button>
        </div>
      </Sheet>

      <Portal isMounted={clockInModal?.isOpen}>
        <Popup
          open={clockInModal?.isOpen}
          title={t("Clock-in Required")}
          description={t("You must be clocked in to access table service. Would you like to clock in now?")}
          type="info"
          okText={t("Clock-in")}
          onCancel={closeClockInModal}
          onOk={handleClockIn}
        />
      </Portal>

      <SwitchUserModal
        open={state.displayActiveUserSheet}
        onCancel={handleCloseActiveUserSheet}
        onOk={handleUpdateActiveUser}
        okDisabled={!isPositiveInteger(state.activeUserCode)}
        userCode={state.activeUserCode}
        onChange={handleActiveUserCodeChange}
        onKeyDown={handleActiveUserCodeEnter}
        autoFocus
      />

      {/* Switch user after table is closed */}
      <SwitchUserModal
        open={switchUserAfterTransaction}
        onCancel={handleLogoutUser}
        cancelText={t("secure.facility.table_service.table_selection.012")}
        onOk={handleUpdateActiveUser}
        okDisabled={!isPositiveInteger(state.activeUserCode)}
        userCode={state.activeUserCode}
        onChange={handleActiveUserCodeChange}
        onKeyDown={handleActiveUserCodeEnter}
        autoFocus
        backDropCancel={false}
      />

      <TransferTableSheet
        modalVisibility={transferTable.isOpen}
        onCancel={() => updateTransferTable({ isOpen: false })}
        onOk={info => void handleTransferTable(info)}
        users={transferTable.users}
      />

      <EditSeatDetails
        editSeatVisible={seatDetails.isOpen}
        onCancel={closeSeatDetails}
        onOkMultiSeat={details => handleUpdateMultiCartDetails(details)}
        prevCartDetails={{
          customer: seatDetails.customer,
          description: seatDetails.description,
        }}
        tableServiceCarts={cartStore.activeTable?.carts}
        selectedSeatId={cartStore.cart?.id}
        onSeatChange={cart => void handleSeatDetailsSeatChange(cart)}
      />

      {sendChits?.isOpen && (
        <TableServiceSendLineItemsModal
          open={sendChits.isOpen}
          selectedIds={getSendChitIds(cartStore.activeTable)}
          onOk={lineItemIds => handleSendChit(lineItemIds)}
          onCancel={cancelSendChits}
          activeTable={cartStore.activeTable}
        />
      )}

      <Portal isMounted={printChitsState?.isOpen}>
        <PrintChit
          printOrderVisible={printChitsState?.isOpen}
          onCancel={cancelPrintChits}
          onOk={chits => {
            void handlePrintReceipt(chits);
          }}
          activeTable={cartStore.activeTable}
        />
      </Portal>

      <CartAuditTrailDrawer
        open={viewCartAudit}
        onClose={() => setViewCartAudit(false)}
        cart={cartStore.cart}
        tableServiceCarts={cartStore.activeTable?.carts}
      />
    </div>
  );
}
